import {
    asTransportFailure,
    defaultRetryPolicy,
    FetchRequest,
    fetchWithRetry,
    NetworkTransportError,
    RequestConstructionError,
} from '~/backend/network-retry';
import { isAbortError } from '~/backend/retryOn202';
import { ConnectorNextContext } from './connector-next-context';

// Relative on purpose: Vite proxies /api to connector-next in dev, and in
// production connector-next serves the SPA itself, so requests are same-origin.
const baseUrl = '';

export type ErrorWrapper<TError> =
    | TError
    | { status: 'unknown'; payload: string };

/**
 * HTTP error carrying PHI-safe fields only. The ProblemDetails `detail` can
 * echo patient identifiers, so it is never read; `message` is the server's
 * `title`, which is a fixed string on every connector-next response.
 */
export class ConnectorNextError extends Error {
    public readonly status: number;

    public constructor(status: number, title: string) {
        super(title);
        this.name = 'ConnectorNextError';
        this.status = status;
    }
}

export type ConnectorNextFetcherOptions<
    TBody,
    THeaders,
    TQueryParams,
    TPathParams,
> = {
    url: string;
    method: string;
    body?: TBody;
    headers?: THeaders;
    queryParams?: TQueryParams;
    pathParams?: TPathParams;
    signal?: AbortSignal;
} & ConnectorNextContext['fetcherOptions'];

/*
 * The type parameters and their `{}` bounds are the signature the
 * generated client (`connector-next-components.ts`) calls with, so they
 * stay as generated: `TError` is passed though not used here.
 */
/* eslint-disable @typescript-eslint/ban-types */
export async function connectorNextFetch<
    TData,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    TError,
    TBody extends {} | FormData | undefined | null,
    THeaders extends {},
    TQueryParams extends {},
    TPathParams extends {},
    /* eslint-enable @typescript-eslint/ban-types */
>({
    url,
    method,
    body,
    headers,
    pathParams,
    queryParams,
    signal,
    retryPolicy,
    retryWindowMs,
    responseTimeoutMs,
}: ConnectorNextFetcherOptions<
    TBody,
    THeaders,
    TQueryParams,
    TPathParams
>): Promise<TData> {
    // Built before the fetch so a missing path parameter throws here instead of
    // being reported as a network error.
    const requestUrl = `${baseUrl}${resolveUrl(url, queryParams, pathParams)}`;
    const requestMethod = method.toUpperCase();

    // Rebuilt per attempt; the body (FormData or source object) is reused and
    // the browser serializes it anew on every send.
    const buildRequest = async (): Promise<FetchRequest> => {
        const requestHeaders: Record<string, string> = {
            'Content-Type': 'application/json',
            ...headers,
        };

        /**
         * As the fetch API is being used, when multipart/form-data is specified
         * the Content-Type header must be deleted so that the browser can set
         * the correct boundary.
         * https://developer.mozilla.org/en-US/docs/Web/API/FormData/Using_FormData_Objects#sending_files_using_a_formdata_object
         */
        if (
            requestHeaders['Content-Type']
                ?.toLowerCase()
                .includes('multipart/form-data')
        ) {
            delete requestHeaders['Content-Type'];
        }

        return {
            url: requestUrl,
            init: {
                signal,
                method: requestMethod,
                body: body
                    ? body instanceof FormData
                        ? body
                        : JSON.stringify(body)
                    : undefined,
                headers: requestHeaders,
            },
        };
    };

    let response: Response;
    try {
        response = await fetchWithRetry(buildRequest, {
            signal,
            // The URL template, never the resolved URL: paths and queries
            // carry DICOM UIDs.
            label: `${requestMethod} ${url}`,
            retryPolicy:
                retryPolicy ?? defaultRetryPolicy(requestMethod, headers),
            retryWindowMs,
            responseTimeoutMs,
        });
    } catch (e) {
        // HTTP errors are handled below. A cancellation, a request that
        // could not be built and a transport failure keep their types;
        // anything else failed before the request went out, which a
        // reconnect cannot fix. Messages are generic (no PHI).
        const failure = asTransportFailure(e);
        if (
            isAbortError(failure) ||
            failure instanceof RequestConstructionError ||
            failure instanceof NetworkTransportError
        ) {
            throw failure;
        }
        throw new Error(
            failure instanceof Error
                ? `Network error (${failure.message})`
                : 'Network error'
        );
    }

    if (!response.ok) {
        // Only the fixed `title` is read from the problem body — never `detail`,
        // which can carry patient identifiers.
        let title = `Request failed (${response.status})`;
        try {
            const problem = (await response.json()) as { title?: string };
            title = problem?.title || title;
        } catch {
            // Empty or non-JSON error body; keep the generic title.
        }
        throw new ConnectorNextError(response.status, title);
    }

    // The body is already buffered by the retrying fetch, so these reads
    // cannot fail on the network.
    if (response.headers.get('content-type')?.includes('json')) {
        return await response.json();
    } else {
        // if it is not a json response, assume it is a blob and cast it to TData
        return (await response.blob()) as unknown as TData;
    }
}

const resolveUrl = (
    url: string,
    queryParams: Record<string, unknown> = {},
    pathParams: Record<string, string> = {}
) => {
    // Nullish query values are dropped: URLSearchParams would serialize them as
    // the literal "undefined"/"null", which the server rejects (an unset enum
    // filter would become a 400).
    //
    // An array value becomes one repeated parameter per element (?f=a&f=b), the
    // OpenAPI `form`/`explode` form the connector's model binder reads. Joining
    // the elements into a single parameter instead would send one value the
    // server cannot parse, so a multi-value filter would 400 rather than filter.
    // An empty array contributes nothing, which is what "no filter" must mean.
    const defined = new URLSearchParams();
    for (const [key, value] of Object.entries(queryParams)) {
        if (value === undefined || value === null) {
            continue;
        }
        if (Array.isArray(value)) {
            for (const element of value) {
                if (element !== undefined && element !== null) {
                    defined.append(key, String(element));
                }
            }
        } else {
            defined.append(key, String(value));
        }
    }
    let query = defined.toString();
    if (query) {
        query = `?${query}`;
    }
    // A missing path parameter throws rather than shipping the literal
    // "undefined" in the URL.
    const path = url.replace(/\{(\w+)\}/g, (_match, name: string) => {
        const value = pathParams[name];
        if (value === undefined || value === null) {
            throw new Error(`Missing path parameter: ${name}`);
        }
        return String(value);
    });
    return path + query;
};
